import { createContext, useContext, useEffect, useState } from 'react';
import { IInviteReviewer } from '../components/Submission/ReviewTab/InviteReviewer/commonTypes';

interface ReviewerInviteListContextType {
  reviewerList: IInviteReviewer[];
  selectedReviewersToInvite: Record<string, boolean>;
  toggleSelectedReviewersToInvite: (email: string) => void;
  setSelectedReviewerToInvite: (email: string, checked: boolean) => void;
  selectAllReviewersToInvite: (checked: boolean) => void;
  addReviewer: (reviewer: IInviteReviewer) => void;
  addReviewersByEmails: (emails: string[]) => void;
  removeReviewersByEmails: (emails: string[]) => void;
  markReviewersInvited: (emails: string[]) => void;
}

const ReviewerInviteListContext = createContext<
  ReviewerInviteListContextType | undefined
>(undefined);

export const useReviewerInviteList = () => {
  const context = useContext(ReviewerInviteListContext);
  if (!context) {
    throw new Error(
      'useReviewerInviteList must be used within a ReviewerInviteListProvider'
    );
  }
  return context;
};

export const ReviewerInviteListProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [reviewerList, setReviewerList] = useState<IInviteReviewer[]>([]);
  const [selectedReviewersToInvite, setSelectedReviewersToInvite] = useState<
    Record<string, boolean>
  >({});

  /* ---------------- Sync selection with reviewer list ---------------- */
  useEffect(() => {
    setSelectedReviewersToInvite(prev => {
      const next: Record<string, boolean> = {};

      reviewerList.forEach(r => {
        // Use previous value if exists, otherwise use computed default
        next[r.email] = prev[r.email] ?? true;
      });

      return next;
    });
  }, [reviewerList]);

  /* ---------------- Reviewer list operations ---------------- */

  const addReviewer = (reviewer: IInviteReviewer) => {
    setReviewerList(prev =>
      prev.some(r => r.email === reviewer.email) ? prev : [...prev, reviewer]
    );
  };

  const addReviewersByEmails = (emails: string[]) => {
    const mapped: IInviteReviewer[] = emails.map(email => ({
      email,
      source: 'manual'
    }));

    setReviewerList(prev => [
      ...prev,
      ...mapped.filter(r => !prev.some(p => p.email === r.email))
    ]);
  };

  const removeReviewersByEmails = (emails: string[]) => {
    setReviewerList(prev => prev.filter(r => !emails.includes(r.email)));
  };

  const markReviewersInvited = (emails: string[]) => {
    setReviewerList(prev =>
      prev.map(r => (emails.includes(r.email) ? { ...r, invited: true } : r))
    );
  };

  /* ---------------- Selection helpers ---------------- */

  const toggleSelectedReviewersToInvite = (email: string) => {
    setSelectedReviewersToInvite(prev => ({
      ...prev,
      [email]: !prev[email]
    }));
  };

  const setSelectedReviewerToInvite = (email: string, checked: boolean) => {
    setSelectedReviewersToInvite(prev => ({
      ...prev,
      [email]: checked
    }));
  };

  const selectAllReviewersToInvite = (checked: boolean) => {
    setSelectedReviewersToInvite(prev => {
      const next = { ...prev };
      reviewerList.forEach(r => {
        next[r.email] = checked;
      });
      return next;
    });
  };

  return (
    <ReviewerInviteListContext.Provider
      value={{
        reviewerList,
        selectedReviewersToInvite,
        toggleSelectedReviewersToInvite,
        setSelectedReviewerToInvite,
        selectAllReviewersToInvite,
        addReviewer,
        addReviewersByEmails,
        removeReviewersByEmails,
        markReviewersInvited
      }}
    >
      {children}
    </ReviewerInviteListContext.Provider>
  );
};
